import { useCallback, useSyncExternalStore } from "react";
import { useFeatureFlag } from "@/shared/featureFlags";
import { useToast } from "@/shared/ui";
import { getSupportChatIdentity } from "../services/supportIdentityService";
import { isTawkConfigured, loadTawkWidget, openTawkChat, TawkCancelledError, TawkTimeoutError } from "../services/tawkService";

// Le header, le profil et le panneau support partagent la même ouverture.
let pendingOpen: Promise<void> | null = null;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
const getIsOpening = () => pendingOpen !== null;
const notify = () => listeners.forEach((listener) => listener());

export function useSupportChat() {
  const enabled = useFeatureFlag("supportChatEnabled");
  const toast = useToast();
  const isOpening = useSyncExternalStore(subscribe, getIsOpening, () => false);

  const openChat = useCallback(async () => {
    if (!enabled) {
      toast.show({ type: "warning", message: "Le chat support est temporairement désactivé." });
      return;
    }
    if (!isTawkConfigured()) {
      toast.show({ type: "warning", message: "Le chat support n'est pas encore configuré." });
      return;
    }
    if (pendingOpen) return pendingOpen;

    // Verrou synchrone : même deux clics avant le prochain rendu React
    // ne déclenchent qu'une seule identification et un seul toast.
    pendingOpen = Promise.resolve().then(async () => {
      try {
        // L'identité et le script sont indépendants : les charger ensemble évite
        // d'ajouter la latence du Gateway à celle du CDN tawk.to.
        const [identityResult, widgetResult] = await Promise.allSettled([
          getSupportChatIdentity(),
          loadTawkWidget(),
        ]);
        if (widgetResult.status === "rejected") throw widgetResult.reason;
        const identity = identityResult.status === "fulfilled" ? identityResult.value : undefined;
        const mode = await openTawkChat(identity);
        if (mode === "anonymous") {
          toast.show({
            type: "warning",
            message: "Le chat est ouvert en mode anonyme. Indique ton nom au support si nécessaire.",
            duration: 5_000,
          });
        }
      } catch (error) {
        if (error instanceof TawkCancelledError) return;
        toast.show({
          type: error instanceof TawkTimeoutError ? "warning" : "danger",
          message: error instanceof TawkTimeoutError
            ? error.message
            : "Impossible d'ouvrir le chat pour le moment. Réessaie dans quelques instants.",
        });
      } finally {
        pendingOpen = null;
        notify();
      }
    });
    notify();
    return pendingOpen;
  }, [enabled, toast]);

  return { openChat, isOpening, isEnabled: enabled };
}
